import { useState, useEffect } from 'react';
import { patchCompany } from '../../../api/company/company';

export const useDepositSettings = (companyData, updateCompanyData = null, setNotification = null) => {
  const [isLoading, setIsLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    console.log('useDepositSettings - Initialization:', {
      hasCompanyData: !!companyData,
      companyId: companyData?.id,
      hasDepositSettings: !!companyData?.depositSettings,
      depositSettingsStructure: companyData?.depositSettings,
      globalEnabled: companyData?.depositSettings?.globalEnabled,
      defaultSettings: companyData?.depositSettings?.defaultSettings
    });

    // Default settings to use if none exist
    const defaultSettings = {
      globalEnabled: false,
      defaultSettings: {
        calculationType: 'duration',
        amount: 0,
        minimumAmount: 0,
        maximumAmount: 0,
      },
      cancellationPolicy: {
        timeframe: 24,
        refundPolicy: '',
        termsAndConditions: '',
      }
    };

    if (companyData?.depositSettings) {
      // Validate and set deposit settings
      const validatedSettings = {
        globalEnabled: companyData.depositSettings.globalEnabled ?? false,
        defaultSettings: {
          calculationType: companyData.depositSettings.defaultSettings?.calculationType || 'duration',
          amount: parseFloat(companyData.depositSettings.defaultSettings?.amount) || 0,
          minimumAmount: parseFloat(companyData.depositSettings.defaultSettings?.minimumAmount) || 0,
          maximumAmount: parseFloat(companyData.depositSettings.defaultSettings?.maximumAmount) || 0,
        },
        cancellationPolicy: {
          timeframe: parseInt(companyData.depositSettings.cancellationPolicy?.timeframe) || 24,
          refundPolicy: companyData.depositSettings.cancellationPolicy?.refundPolicy || '',
          termsAndConditions: companyData.depositSettings.cancellationPolicy?.termsAndConditions || '',
        }
      };

      console.log('useDepositSettings - Validated Settings:', validatedSettings);
      setSettings(validatedSettings);
    } else {
      // Set default settings if no deposit settings exist
      console.log('useDepositSettings - Using Default Settings:', defaultSettings);
      setSettings(defaultSettings);
    }
  }, [companyData]);

  const saveSettings = async (newSettings) => {
    if (!companyData || !updateCompanyData || !setNotification) {
      console.error('Missing required dependencies for saving settings');
      return;
    }

    setIsLoading(true);
    try {
      console.log('Saving deposit settings:', {
        companyId: companyData.id,
        newSettings
      });

      // Ensure the newSettings object has the correct structure
      const validatedSettings = {
        globalEnabled: newSettings.globalEnabled ?? false,
        defaultSettings: {
          calculationType: newSettings.defaultSettings?.calculationType || 'duration',
          amount: parseFloat(newSettings.defaultSettings?.amount) || 0,
          minimumAmount: parseFloat(newSettings.defaultSettings?.minimumAmount) || 0,
          maximumAmount: parseFloat(newSettings.defaultSettings?.maximumAmount) || 0,
        },
        cancellationPolicy: {
          timeframe: parseInt(newSettings.cancellationPolicy?.timeframe) || 24,
          refundPolicy: newSettings.cancellationPolicy?.refundPolicy || '',
          termsAndConditions: newSettings.cancellationPolicy?.termsAndConditions || '',
        }
      };

      const company = await patchCompany({
        companyId: companyData.id,
        company: { depositSettings: validatedSettings },
      });

      updateCompanyData({
        ...companyData,
        ...company,
        depositSettings: validatedSettings
      });

      console.log('Deposit settings saved successfully:', validatedSettings);

      setNotification({
        open: true,
        message: 'Deposit settings saved successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving deposit settings:', error);
      setNotification({
        open: true,
        message: 'Error saving deposit settings: ' + error.message,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const calculateServiceDeposit = (service) => {
    console.log('calculateServiceDeposit - Input:', {
      serviceId: service?.id,
      serviceName: service?.name,
      servicePrice: service?.price,
      serviceDuration: service?.duration,
      hasSelectedPackage: !!service?.selectedPackage,
      selectedPackage: service?.selectedPackage ? {
        id: service.selectedPackage.id,
        name: service.selectedPackage.name,
        price: service.selectedPackage.price,
        duration: service.selectedPackage.duration
      } : null,
      hasServiceDepositSettings: !!service?.depositSettings,
      serviceDepositSettings: service?.depositSettings,
      globalEnabled: settings?.globalEnabled,
      defaultSettings: settings?.defaultSettings,
      addOns: service?.addOns,
      packages: service?.packages
    });

    if (!settings?.globalEnabled || !service) {
      console.log('Deposit calculation skipped:', {
        reason: !settings?.globalEnabled ? 'global deposits disabled' : 'no service provided',
        globalEnabled: settings?.globalEnabled,
        hasService: !!service
      });
      return 0;
    }

    // Use service-specific settings if available, otherwise use defaults
    const depositSettings = service.depositSettings?.isEnabled ? 
      service.depositSettings : 
      settings.defaultSettings;

    console.log('Selected deposit settings:', {
      usingServiceSpecific: !!service.depositSettings?.isEnabled,
      depositSettings,
      calculationType: depositSettings?.calculationType,
      amount: depositSettings?.amount,
      minimumAmount: depositSettings?.minimumAmount,
      maximumAmount: depositSettings?.maximumAmount
    });

    if (!depositSettings) {
      console.log('No deposit settings found');
      return 0;
    }

    let depositAmount = 0;
    
    // Check if a package is selected
    const hasSelectedPackage = !!service.selectedPackage;
    
    // Calculate total price including add-ons and package (if selected)
    const basePrice = hasSelectedPackage 
      ? parseFloat(service.selectedPackage.price) || 0
      : parseFloat(service.price) || 0;
      
    const addOnsPrice = (service.selectedAddons || service.addOns || []).reduce((total, addon) => 
      total + (parseFloat(addon.price) || 0), 0);
    
    // Calculate total duration including add-ons and package (if selected)
    const baseDuration = hasSelectedPackage 
      ? parseInt(service.selectedPackage.duration) || 0
      : parseInt(service.duration) || 0;
      
    const addOnsDuration = (service.selectedAddons || service.addOns || []).reduce((total, addon) => 
      total + (parseInt(addon.duration) || 0), 0);

    const totalPrice = basePrice + addOnsPrice;
    const totalDuration = baseDuration + addOnsDuration;

    console.log('Price and Duration Calculations:', {
      hasSelectedPackage,
      packagePrice: hasSelectedPackage ? service.selectedPackage.price : null,
      packageDuration: hasSelectedPackage ? service.selectedPackage.duration : null,
      basePrice,
      addOnsPrice,
      totalPrice,
      baseDuration,
      addOnsDuration,
      totalDuration
    });

    switch (depositSettings.calculationType) {
      case 'fixed':
        depositAmount = parseFloat(depositSettings.amount) || 0;
        break;
      case 'percentage':
        depositAmount = (totalPrice * (parseFloat(depositSettings.amount) || 0)) / 100;
        break;
      case 'duration':
        // Convert total duration to hours and multiply by hourly rate
        depositAmount = (totalDuration / 60) * (parseFloat(depositSettings.amount) || 0);
        break;
      default:
        depositAmount = 0;
    }

    // Apply minimum and maximum limits
    const originalAmount = depositAmount;
    if (depositSettings.minimumAmount && depositAmount < depositSettings.minimumAmount) {
      depositAmount = parseFloat(depositSettings.minimumAmount);
    }
    if (depositSettings.maximumAmount && depositAmount > depositSettings.maximumAmount) {
      depositAmount = parseFloat(depositSettings.maximumAmount);
    }

    console.log('Deposit calculation details:', {
      calculationType: depositSettings.calculationType,
      hasSelectedPackage,
      basePrice,
      addOnsPrice,
      totalPrice,
      baseDuration,
      addOnsDuration,
      totalDuration,
      originalAmount,
      afterLimits: Math.round(depositAmount * 100) / 100,
      minimumApplied: depositAmount === depositSettings.minimumAmount,
      maximumApplied: depositAmount === depositSettings.maximumAmount
    });

    return Math.round(depositAmount * 100) / 100; // Round to 2 decimal places
  };

  return {
    settings,
    setSettings,
    saveSettings,
    isLoading,
    calculateServiceDeposit
  };
};

export default useDepositSettings; 